"use client"

import clsx from "clsx"
import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react"
import { useEffect, useMemo, useRef, useState } from "react"
import FocusLock from "react-focus-lock"
import {
  npPageGapClassName,
  npPagePbClassName,
  npPagePtClassName,
  npPagePxClassName,
} from "src/components/np-design/components/constants"
import { NpButton } from "src/components/np-design/components/NpButton"
import { NpCarouselArrow } from "src/components/np-design/components/NpCarouselArrow"
import { NpIcon } from "src/components/np-design/components/NpIcon"
import { NpPaginationDots } from "src/components/np-design/components/NpPaginationDots"
import { Img } from "src/components/unstyled/Img"
import { ResponsiveImage } from "src/components/unstyled/ResponsiveImage"
import { useBackwardForwardCacheRestored } from "src/hooks/useBackwardForwardCacheRestored"
import { useEventCallback } from "src/hooks/useEventCallback"
import { useIsMinBreakpoint } from "src/hooks/useIsMinBreakpoint"
import { useKeydown } from "src/hooks/useKeydown"
import { usePreventBodyScroll } from "src/hooks/usePreventBodyScroll"
import { ImageData } from "src/types/images"
import { emblaOptions, useEmbla } from "src/utilities/embla"
import { ProductImageSize, toProductImageCdnUrl } from "src/utilities/productImages"
import { smoothScrollToTop } from "src/utilities/scroll"

type EmblaApi = UseEmblaCarouselType[1]

export type SlideImageFit = "contain" | "cover"

type SlideProps = ImageData & {
  size: ProductImageSize
}

const Slide = ({ src, alt, loading, size }: SlideProps) => {
  return (
    <li className="embla__slide max-h-full min-w-0 shrink-0 grow-0 basis-full pl-4">
      <ResponsiveImage
        src={toProductImageCdnUrl(src, size)}
        alt={alt}
        loading={loading}
        width={size}
        height={size}
        className="embla__slide_content aspect-square rounded-3xl bg-gray-50 object-contain"
        style={{ maxWidth: size + "px" }}
      />
    </li>
  )
}

const Slides = ({ slides, imageSize }: { slides: ImageData[]; imageSize: number }) => {
  return (
    <ul className="embla__container -ml-4 flex max-h-full">
      {slides.map((image, index) => {
        return (
          <Slide
            key={index}
            src={image.src}
            alt={image.alt || `Bild ${index + 1}`}
            loading={image.loading}
            size={imageSize}
          />
        )
      })}
    </ul>
  )
}

const ThumbnailsNavigation = ({
  embla,
  slides,
  activeIndex: parentIndex,
}: {
  embla: EmblaApi
  slides: ImageData[]
  activeIndex: number
}) => {
  const [miniRef, emblaMini] = useEmblaCarousel({ ...emblaOptions, slidesToScroll: 1 })

  useEffect(() => {
    if (!emblaMini) return

    const miniIndex = emblaOptions.loop ? parentIndex : Math.max(0, parentIndex - 1)

    emblaMini.scrollTo(miniIndex)
  }, [parentIndex, emblaMini])

  return (
    <div ref={miniRef} className="embla__viewport overflow-hidden rounded-lg">
      <div className="embla__container -ml-2 flex max-h-full rounded-lg p-px">
        {slides.map((slide, index) => {
          const active = index === parentIndex

          return (
            <div key={index} className="embla__slide max-h-25 min-w-0 shrink-0 grow-0 basis-27 pl-2">
              <button
                type="button"
                onClick={() => {
                  embla?.scrollTo(index)
                  smoothScrollToTop()
                }}
                className={clsx(
                  "embla__slide_content flex aspect-square items-center justify-center overflow-hidden rounded-lg border transition-colors",
                  {
                    "border-np-gray25": !active,
                    "border-np-green": active,
                  },
                )}
              >
                <Img
                  src={toProductImageCdnUrl(slide.src, ProductImageSize.Tiny)}
                  alt={slide.alt}
                  className="size-full rounded-lg object-cover"
                />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const Fullscreen = ({
  slides,
  activeIndex: parentIndex,
  onCloseAtIndex,
}: {
  slides: ImageData[]
  activeIndex: number
  onCloseAtIndex: (fullscreenEmbla: number) => void
}) => {
  const { ref, embla: emblaFullscreen, activeIndex: fullscreenIndex } = useEmbla()

  useEffect(() => {
    if (!emblaFullscreen) return

    const jump = true
    emblaFullscreen.scrollTo(parentIndex, jump)
  }, [emblaFullscreen, parentIndex])

  const hide = useEventCallback(() => onCloseAtIndex(fullscreenIndex))

  usePreventBodyScroll(true)
  useKeydown("Escape", hide)
  useBackwardForwardCacheRestored(hide)

  const imageSize = ProductImageSize.Large * 2

  return (
    <FocusLock autoFocus returnFocus>
      <div
        className={clsx(
          npPagePxClassName,
          npPagePtClassName,
          npPagePbClassName,
          npPageGapClassName,
          "z-modal fixed inset-0 flex flex-col items-center bg-white",
        )}
      >
        <div className="absolute top-0 right-0 z-1 m-4">
          <NpButton
            iconNameLeft="close"
            variant="ghost"
            color="gray75"
            size="md"
            onClick={hide}
            aria-label="Schließen"
            title="Schließen"
          />
        </div>

        <div ref={ref} className="relative mx-auto w-full max-w-5xl overflow-hidden rounded-3xl">
          <Slides slides={slides} imageSize={imageSize} />
        </div>

        {slides.length > 1 && (
          <div className="mx-auto w-full max-w-5xl shrink-0">
            <ThumbnailsNavigation embla={emblaFullscreen} slides={slides} activeIndex={fullscreenIndex} />
          </div>
        )}
      </div>
    </FocusLock>
  )
}

export type NpProductImagesProps = {
  name: string
  images: string[]
}

export const NpProductImages = ({ name, images }: NpProductImagesProps) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { ref, embla, scrollSnaps, activeIndex, hasPrev, hasNext, justifyCenter } = useEmbla()
  const [fullscreen, setFullscreen] = useState(false)
  const hasFullscreen = useIsMinBreakpoint("sm")

  const slides = useMemo(() => {
    return images.map((image, index) => {
      return {
        src: image,
        alt: `${name} (Bild ${index + 1})`,
        loading: index === 0 ? "eager" : undefined,
      } satisfies ImageData
    })
  }, [name, images])

  const onCloseAtIndex = (fullscreenIndex: number) => {
    setFullscreen(false)

    const jump = true
    embla?.scrollTo(fullscreenIndex, jump)
  }

  const Container = hasFullscreen ? "button" : "div"

  return (
    <div ref={sectionRef} className="flex flex-col gap-4">
      <div className="embla relative flex w-full max-w-150 flex-col gap-4">
        <Container
          ref={ref}
          onClick={hasFullscreen ? () => setFullscreen(true) : undefined}
          className={clsx("embla__viewport overflow-hidden rounded-3xl", {
            "cursor-zoom-in": hasFullscreen,
          })}
          data-testid={hasFullscreen ? "CarouselFullscreenButton" : undefined}
        >
          <Slides slides={slides} imageSize={ProductImageSize.Large} />
        </Container>

        {hasPrev && (
          <div className="absolute top-1/2 left-4 hidden -translate-y-1/2 sm:block">
            <NpCarouselArrow side="left" onClick={() => embla?.scrollPrev()} />
          </div>
        )}
        {hasNext && (
          <div className="absolute top-1/2 right-4 hidden -translate-y-1/2 sm:block">
            <NpCarouselArrow side="right" onClick={() => embla?.scrollNext()} />
          </div>
        )}
        {hasFullscreen && <NpIcon name="zoom_in" className="text-np-green absolute right-0 bottom-0 m-4 size-9" />}
      </div>

      {!justifyCenter && (
        <div className="hidden w-full max-w-150 md:block">
          <ThumbnailsNavigation embla={embla} slides={slides} activeIndex={activeIndex} />
        </div>
      )}
      {!justifyCenter && (
        <div className="flex w-full justify-center sm:hidden">
          <NpPaginationDots count={scrollSnaps.length} activeIndex={activeIndex} />
        </div>
      )}
      {hasFullscreen && fullscreen && (
        <Fullscreen slides={slides} activeIndex={activeIndex} onCloseAtIndex={onCloseAtIndex} />
      )}
    </div>
  )
}
